// Feature A — trajectory analog engine (first cut, per design consensus).
//
// Given a live storm's track, auto-derive up to 3 boxes from the track SHAPE
// (fractions, not fixed time) and find historical storms on a similar path:
//   • Box A = origin. matchMode="origin" (default) → GENESIS semantics: an analog
//     qualifies only if its own first-TD fix lands in A (it was *born* there).
//     matchMode="transit" → the storm need only PASS THROUGH A (Bayesian-style),
//     admitting storms born elsewhere that tracked through the origin region.
//   • Box B (mid) / Box C (current) = TRANSIT, in temporal order after A.
// Direction is carried implicitly by the ordered sequence (loop-safe; no heading).
//
// Adaptive count by distance flown from genesis:
//   tiny track → 1 transit box (plain climatology, "not enough track yet")
//   some track → 2 boxes (genesis → current)
//   mature     → 3 boxes (genesis → mid → current)
//
// Sizing is quality-first: generous-but-clamped, grows gently with track extent,
// scaled by a manual `tightness`. We accept small N (true analogs > many weak ones)
// and surface the count. The heatmap/lines show the DOWNSTREAM (post-current)
// segment of matches — "where storms like this one went next".

import type { Box, Storm, TrackPoint } from "./types";
import { pointInBox } from "./probability";

export interface LivePoint {
  lat: number;
  lon: number;
}

export interface AnalogStorm {
  storm_id: string;
  name: string;
  year: number;
  ss_category: string;
  peak_vmax: number;
}

export interface AnalogPath {
  path: [number, number][];
  storm_id: string;
  name: string;
  year: number;
}

export type AnalogMatchMode = "origin" | "transit";

export interface AnalogResult {
  boxes: Box[];
  genesisMode: boolean; // false = young storm → single transit box (climatology)
  matchMode: AnalogMatchMode; // how box A was matched (origin = born-in, transit = passed-through)
  count: number;
  hurricanes: number;
  analogs: AnalogStorm[]; // matched storms (for the right-panel list)
  downstreamPoints: { lon: number; lat: number }[];
  downstreamPaths: AnalogPath[]; // downstream segments carrying storm identity (hover)
}

const TC_STATUS = new Set(["TD", "TS", "HU", "SD", "SS"]);

function distNm(a: LivePoint, b: LivePoint): number {
  const R = 3440.065;
  const dlat = ((b.lat - a.lat) * Math.PI) / 180;
  const dlon = ((b.lon - a.lon) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dlat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dlon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function boxAround(lat: number, lon: number, radiusDeg: number): Box {
  const cosLat = Math.max(0.2, Math.cos((lat * Math.PI) / 180));
  const lonR = radiusDeg / cosLat; // keep the box roughly circular in real distance
  return { lat_min: lat - radiusDeg, lat_max: lat + radiusDeg, lon_min: lon - lonR, lon_max: lon + lonR };
}

/** Index of the storm's genesis fix: first TD, else first classified TC fix, else 0. */
function genesisIndex(track: TrackPoint[]): number {
  for (let i = 0; i < track.length; i++) if (track[i].status === "TD") return i;
  for (let i = 0; i < track.length; i++) if (TC_STATUS.has(track[i].status)) return i;
  return 0;
}

function firstFixInBoxFrom(track: TrackPoint[], box: Box, start: number): number {
  for (let i = start; i < track.length; i++) {
    if (pointInBox(track[i].lat, track[i].lon, box)) return i;
  }
  return -1;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Cumulative great-circle distance (nm) at each fix along the track. */
function cumulativeNm(live: LivePoint[]): number[] {
  const cum = [0];
  for (let i = 1; i < live.length; i++) cum.push(cum[i - 1] + distNm(live[i - 1], live[i]));
  return cum;
}

/** The fix at a given fraction of cumulative DISTANCE (not fix count). */
function fixAtDistanceFraction(live: LivePoint[], cum: number[], frac: number): LivePoint {
  const target = cum[cum.length - 1] * frac;
  for (let i = 0; i < cum.length; i++) if (cum[i] >= target) return live[i];
  return live[live.length - 1];
}

/** Approximate separation in degrees between two box centres. */
function centerSepDeg(a: LivePoint, b: LivePoint): number {
  const dlat = b.lat - a.lat;
  const dlon = (b.lon - a.lon) * Math.cos((((a.lat + b.lat) / 2) * Math.PI) / 180);
  return Math.sqrt(dlat * dlat + dlon * dlon);
}

/**
 * Derive the box set from the live track. Anchors sit at fractions of cumulative
 * DISTANCE (so a storm that lingers near genesis then races away doesn't bunch
 * its anchors), and the box COUNT is emergent from a no-overlap rule: include the
 * mid waypoint only if it clears both neighbours by more than the sum of radii;
 * collapse to a single climatology box if even genesis and current would overlap
 * (very short track). This retires the old fixed nm thresholds.
 */
function deriveBoxes(live: LivePoint[], tightness: number): { boxes: Box[]; genesisMode: boolean } {
  const cum = cumulativeNm(live);
  const trackLen = cum[cum.length - 1];
  const genesis = live[0];
  const current = live[live.length - 1];

  // generous base, grows gently with how far it has flown, × tightness.
  const growth = clamp((trackLen / 1000) * 1.0, 0, 1.5); // up to +1.5°
  const rC = clamp((2.0 + growth) * tightness, 1.0, 6.0);
  const rA = clamp(rC * 1.25, 1.0, 7.0); // origin a touch larger

  // genesis & current too close → not enough separation to anchor a trajectory
  if (centerSepDeg(genesis, current) < rA + rC) {
    return { boxes: [boxAround(current.lat, current.lon, rC)], genesisMode: false };
  }

  const A = boxAround(genesis.lat, genesis.lon, rA);
  const C = boxAround(current.lat, current.lon, rC);

  // mid waypoint at 50% of DISTANCE — included only if it clears both neighbours
  const mid = fixAtDistanceFraction(live, cum, 0.5);
  const rB = rC;
  const clearsA = centerSepDeg(genesis, mid) > rA + rB;
  const clearsC = centerSepDeg(mid, current) > rB + rC;
  if (clearsA && clearsC) {
    return { boxes: [A, boxAround(mid.lat, mid.lon, rB), C], genesisMode: true };
  }
  return { boxes: [A, C], genesisMode: true };
}

export function computeAnalogs(
  live: LivePoint[],
  tracks: Storm[],
  tightness = 1.0,
  matchMode: AnalogMatchMode = "origin",
): AnalogResult | null {
  if (!live || live.length < 2 || tracks.length === 0) return null;

  const { boxes, genesisMode } = deriveBoxes(live, tightness);
  const downstreamPoints: { lon: number; lat: number }[] = [];
  const downstreamPaths: AnalogPath[] = [];
  const analogs: AnalogStorm[] = [];
  let count = 0;
  let hurricanes = 0;

  for (const storm of tracks) {
    const t = storm.track;
    let lastIdx: number;

    if (genesisMode) {
      // Box A test: "origin" requires the storm to be BORN in A (its genesis fix
      // lands in A); "transit" only requires it to PASS THROUGH A (Bayesian-style,
      // any birthplace). Boxes B..C are always ordered transit after A.
      let idx: number;
      if (matchMode === "transit") {
        const fa = firstFixInBoxFrom(t, boxes[0], 0);
        if (fa === -1) continue;
        idx = fa;
      } else {
        const gi = genesisIndex(t);
        if (!pointInBox(t[gi].lat, t[gi].lon, boxes[0])) continue; // born in A
        idx = gi;
      }
      let ok = true;
      for (let b = 1; b < boxes.length; b++) {
        const fi = firstFixInBoxFrom(t, boxes[b], idx + 1); // ordered, after prior box
        if (fi === -1) { ok = false; break; }
        idx = fi;
      }
      if (!ok) continue;
      lastIdx = idx; // entry into the current box
    } else {
      const fi = firstFixInBoxFrom(t, boxes[0], 0);
      if (fi === -1) continue;
      lastIdx = fi;
    }

    count++;
    if (storm.classification === "HU") hurricanes++;
    analogs.push({
      storm_id: storm.storm_id, name: storm.name, year: storm.year,
      ss_category: storm.ss_category, peak_vmax: storm.peak_vmax,
    });
    const path: [number, number][] = [];
    for (let i = lastIdx; i < t.length; i++) {
      downstreamPoints.push({ lon: t[i].lon, lat: t[i].lat });
      path.push([t[i].lon, t[i].lat]);
    }
    if (path.length >= 2) {
      downstreamPaths.push({ path, storm_id: storm.storm_id, name: storm.name, year: storm.year });
    }
  }

  analogs.sort((a, b) => b.year - a.year);
  downstreamPaths.sort((a, b) => b.year - a.year);
  return { boxes, genesisMode, matchMode, count, hurricanes, analogs, downstreamPoints, downstreamPaths };
}
